/**
 * Haptic feedback utilities using the Natively SDK.
 * All functions are safe to call on any platform — they silently no-op
 * when running outside a Natively-wrapped native app.
 */

// ── Type-safe Natively haptic API surface ────────────────────────────

type ImpactStyle = "LIGHT" | "MEDIUM" | "HEAVY" | "RIGID" | "SOFT";
type NotificationType = "SUCCESS" | "ERROR" | "WARNING";

interface NativelyHaptics {
  hapticImpact(style: ImpactStyle): void;
  hapticNotification(type: NotificationType): void;
  hapticPattern(pattern: string, delay: number): void;
}

// ── Preference helpers ────────────────────────────────────────────────

const HAPTICS_PREF_KEY = "haptics_enabled";

/** Whether the user has haptics enabled (defaults to true). */
export const isHapticsEnabled = (): boolean => {
  if (typeof window === "undefined") return false;
  const stored = localStorage.getItem(HAPTICS_PREF_KEY);
  return stored === null ? true : stored === "true";
};

/** Persist the user's haptics preference. */
export const setHapticsEnabled = (enabled: boolean) => {
  localStorage.setItem(HAPTICS_PREF_KEY, String(enabled));
};

/** Returns the Natively haptic bridge, or null when unavailable or disabled. */
const getNatively = (): NativelyHaptics | null => {
  if (!isHapticsEnabled()) return null;
  if (typeof window !== "undefined" && (window as any).natively) {
    const n = (window as any).natively as any;
    if (typeof n.hapticImpact === "function") return n as NativelyHaptics;
  }
  return null;
};

// ── Primitive wrappers ───────────────────────────────────────────────

/** Single impact tap. */
export const hapticImpact = (style: ImpactStyle = "MEDIUM") => {
  getNatively()?.hapticImpact(style);
};

/** Notification-style feedback (success / error / warning). */
export const hapticNotification = (type: NotificationType = "SUCCESS") => {
  getNatively()?.hapticNotification(type);
};

/** Arbitrary pattern string.  See Natively docs for pattern syntax. */
export const hapticPattern = (pattern: string, delay = 0.1) => {
  getNatively()?.hapticPattern(pattern, delay);
};

// ── Semantic helpers used throughout the app ─────────────────────────

/** Light tap — +1 / −1 buttons, minor UI actions. */
export const hapticTap = () => hapticImpact("LIGHT");

/** Medium tap — toggle, confirming a quick action. */
export const hapticToggle = () => hapticImpact("MEDIUM");

/** Heavy single hit — big confirmation moment. */
export const hapticHeavy = () => hapticImpact("HEAVY");

/** Success notification — post completed, values accepted, etc. */
export const hapticSuccess = () => hapticNotification("SUCCESS");

/** Charge-up pattern that crescendos, then a heavy hit at the end.
 *  Use for "Adding..." → done transitions.  */
export const hapticChargeUp = () => {
  hapticPattern("..oo-OO-X", 0.1);
};

/** Small charge-up + explode — pin to dashboard, widget built, etc. */
export const hapticChargeExplode = () => {
  hapticPattern(".oO-X", 0.08);
};

/** Celebration pattern — tutorial complete, milestone. */
export const hapticCelebration = () => {
  hapticPattern("oO-Oo-oO-X", 0.08);
};

/** Subtle soft tap — new AI message, minor state change. */
export const hapticSoft = () => hapticImpact("SOFT");

/** Reaction tap — reacting to a chart or progress pic. */
export const hapticReaction = () => hapticImpact("RIGID");
